import { FC, useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Box, Card, Stack, TableContainer, Typography } from '@mui/material'

import ConfirmDialog from '../../../components/ConfirmDialog/ConfirmDialog'
import { GRTable, GRTableColumn, SortOrder } from '../../../components/GRTable/GRTable'
import usePage from '../../../hooks/usePage'
import pageService from '../../../services/PageService'
import { ConfirmDialogData } from '../../../types/ConfirmDialogData'
import { useGetInternalFeatureMapQuery } from '../../api/features'
import { useDeleteKeywordMutation, useGetInternalKeywordsQuery, useSaveKeywordMutation } from '../../api/keywords'
import { useGetLanguageMapsQuery } from '../../api/language'
import { InternalListToolbar } from '../../components/InternalListToolbar/InternalListToolbar'
import { QueryStateBoundary } from '../../components/QueryStateBoundary/QueryStateBoundary'
import { rowActionsColumn } from '../../components/RowActions/RowActions'
import { KeywordEditDialog } from '../../features/keywords/components/KeywordEditDialog/KeywordEditDialog'
import {
  buildKeywordRows,
  getActiveFeatures,
  KeywordRow,
  keywordPriorityLabelKeys,
  toKeywordClone,
  toKeywordDraft,
} from '../../features/keywords/helpers/keywordHelpers'
import { KeywordMutation, KeywordPriority } from '../../features/keywords/types/InternalKeyword'
import { useMutationFeedback } from '../../hooks/useMutationFeedback'

// The legacy app loads features for its default market; only `active` and `name` are read from them.
const featureMapMarketIso = 'us'

type CustomProps = {
  onEdit: (row: KeywordRow) => void
  onClone: (row: KeywordRow) => void
  onDelete: (row: KeywordRow) => void
}

type DialogState = { open: boolean; keyword?: KeywordMutation }

export const KeywordsPage: FC = () => {
  usePage(pageService.getInternalPageWithId('internal-keywords'))
  const { t } = useTranslation()
  const { run } = useMutationFeedback()
  const keywordsQuery = useGetInternalKeywordsQuery()
  const languageMapsQuery = useGetLanguageMapsQuery()
  const featureMapQuery = useGetInternalFeatureMapQuery({ marketIso: featureMapMarketIso })
  const [saveKeyword, { isLoading: isSaving }] = useSaveKeywordMutation()
  const [deleteKeyword] = useDeleteKeywordMutation()
  const [dialogState, setDialogState] = useState<DialogState>({ open: false })
  const [confirmDialogData, setConfirmDialogData] = useState<ConfirmDialogData<KeywordRow>>()

  const activeFeatures = useMemo(() => getActiveFeatures(featureMapQuery.data), [featureMapQuery.data])
  const rows = useMemo(
    () => buildKeywordRows(keywordsQuery.data ?? [], languageMapsQuery.data, activeFeatures),
    [keywordsQuery.data, languageMapsQuery.data, activeFeatures]
  )
  const isLoading = keywordsQuery.isLoading || languageMapsQuery.isLoading || featureMapQuery.isLoading
  const isError = keywordsQuery.isError || languageMapsQuery.isError || featureMapQuery.isError

  const tableColumns: GRTableColumn<KeywordRow, CustomProps>[] = useMemo(
    () => [
      {
        labelAccessor: t('internal-service-management:keywords_name_en'),
        accessor: ({ row }) => <Typography fontWeight="bold">{row.names.en}</Typography>,
        sortable: true,
        sortAccessor: ({ row }) => row.names.en?.toLowerCase() ?? '',
        // GRTable compares strings in reverse, so DESC lists names A to Z.
        sortOrder: SortOrder.DESC,
        cellProps: { align: 'left' },
        headerCellProps: { align: 'left', sx: { minWidth: 180 } },
      },
      {
        labelAccessor: t('internal-service-management:keywords_name_ja'),
        accessor: ({ row }) => row.names.ja,
        sortable: true,
        sortAccessor: ({ row }) => row.names.ja ?? '',
        cellProps: { align: 'left' },
        headerCellProps: { align: 'left', sx: { minWidth: 160 } },
      },
      {
        labelAccessor: t('internal-service-management:keywords_name_zh'),
        accessor: ({ row }) => row.names.zh,
        sortable: true,
        sortAccessor: ({ row }) => row.names.zh ?? '',
        cellProps: { align: 'left' },
        headerCellProps: { align: 'left', sx: { minWidth: 120 } },
      },
      {
        labelAccessor: t('internal-service-management:keywords_linked_features'),
        accessor: ({ row }) => (
          <Stack>
            {row.linkedFeatureNames.map((featureName) => (
              <Typography key={featureName} variant="body2">
                {featureName}
              </Typography>
            ))}
          </Stack>
        ),
        cellProps: { align: 'left' },
        headerCellProps: { align: 'left', sx: { minWidth: 240 } },
      },
      {
        labelAccessor: t('internal-service-management:keywords_priority'),
        accessor: ({ row }) => {
          const labelKey = keywordPriorityLabelKeys[row.keyword.priority as KeywordPriority]
          return labelKey ? t(labelKey) : row.keyword.priority
        },
        sortable: true,
        sortAccessor: ({ row }) => row.keyword.priority,
        cellProps: { align: 'left' },
        headerCellProps: { align: 'left' },
      },
      rowActionsColumn<KeywordRow, CustomProps>({
        width: 150,
        actions: ({ row, customTableProps }) => [
          { type: 'edit', label: t('internal-service-management:keywords_edit'), onClick: () => customTableProps?.onEdit(row) },
          { type: 'clone', label: t('internal-service-management:keywords_clone'), onClick: () => customTableProps?.onClone(row) },
          { type: 'delete', label: t('internal-service-management:keywords_delete'), onClick: () => customTableProps?.onDelete(row) },
        ],
      }),
    ],
    [t]
  )
  const [columns, setColumns] = useState(tableColumns)

  const customProps = useMemo<CustomProps>(
    () => ({
      onEdit: (row) => setDialogState({ open: true, keyword: toKeywordDraft(row) }),
      onClone: (row) => setDialogState({ open: true, keyword: toKeywordClone(row) }),
      onDelete: (row) =>
        setConfirmDialogData({
          title: t('internal-service-management:keywords_delete'),
          confirmText: t('internal-service-management:keywords_delete_confirm', { name: row.names.en }),
          actionText: t('internal-common:delete'),
          destructiveAction: true,
          data: row,
        }),
    }),
    [t]
  )

  const handleSave = useCallback(
    async (keyword: KeywordMutation) => {
      const saved = await run(saveKeyword(keyword).unwrap(), {
        success: t('internal-service-management:keywords_saved', { name: keyword.name }),
        error: t('internal-service-management:keywords_save_failed'),
      })
      if (saved.ok) setDialogState({ open: false })
    },
    [run, saveKeyword, t]
  )

  const handleConfirmDelete = useCallback(
    async (confirmed?: ConfirmDialogData<KeywordRow>) => {
      setConfirmDialogData(undefined)
      const row = confirmed?.data
      if (!row) return

      await run(deleteKeyword(row.keyword.id).unwrap(), {
        success: t('internal-service-management:keywords_deleted', { name: row.names.en }),
        error: t('internal-service-management:keywords_delete_failed'),
      })
    },
    [deleteKeyword, run, t]
  )

  return (
    <Box>
      <InternalListToolbar add={{ label: t('internal-service-management:keywords_add'), onClick: () => setDialogState({ open: true }) }} />

      <QueryStateBoundary isLoading={isLoading} isError={isError} errorText={t('internal-service-management:keywords_load_failed')}>
        <TableContainer component={Card}>
          <GRTable
            rows={rows}
            columns={columns}
            onColumnsUpdated={setColumns}
            rowIdKey={(row) => row.keyword.id}
            customProps={customProps}
            noRowsLabel={t('internal-common:no_rows')}
            striped
            hoverable
          />
        </TableContainer>
      </QueryStateBoundary>

      <KeywordEditDialog
        open={dialogState.open}
        keyword={dialogState.keyword}
        features={activeFeatures}
        isSaving={isSaving}
        onClose={() => setDialogState({ open: false })}
        onSave={handleSave}
      />
      <ConfirmDialog open={!!confirmDialogData} confirmDialogData={confirmDialogData} onClose={handleConfirmDelete} />
    </Box>
  )
}
